import { YokaiInput } from '../types/yokai';
import { API_SETTINGS } from '../constants/config';
import { YOKAI_GENERATION_SYSTEM_PROMPT, generateYokaiUserPrompt } from '../constants/prompts';
import { logger } from '../utils/logger';
import { handleOpenAiError } from '../utils/errorHandler';
import { validateYokaiInput } from '../utils/validation';

export interface YokaiDataGenerationResult {
  name: string;
  description: string;
  likes: string;
  dislikes: string;
  catchphrase: string;
  imagePrompt: string;
}


/**
 * 妖怪の全データを一括生成（名前・説明・好み・画像プロンプト）
 * サーバーサイドプロキシ経由でOpenAI APIを呼び出す
 */
export const generateYokaiData = async (yokaiInput: YokaiInput): Promise<YokaiDataGenerationResult> => {
  try {
    const validatedInput = validateYokaiInput(yokaiInput);
    logger.apiRequest('OpenAI', 'yokai-data-generation', validatedInput);

    const userPrompt = generateYokaiUserPrompt(yokaiInput);

    // サーバーサイドプロキシ経由でAPIを呼び出す
    const response = await fetch('/api/openai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: YOKAI_GENERATION_SYSTEM_PROMPT },
          { role: 'user', content: userPrompt }
        ],
        max_tokens: API_SETTINGS.OPENAI.MAX_TOKENS,
        temperature: API_SETTINGS.OPENAI.TEMPERATURE,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).error || `APIエラー (${response.status})`);
    }

    const data = await response.json() as any;
    const content = data.choices?.[0]?.message?.content;

    if (!content) {
      throw new Error('OpenAI APIからの応答が空です');
    }

    logger.debug('OPENAI_API', 'API応答受信', { responseLength: content.length });

    // JSONパース
    try {
      // レスポンスからJSONを抽出（```jsonブロックがある場合も対応）
      let jsonString = content.trim();
      if (jsonString.includes('```json')) {
        const match = jsonString.match(/```json\s*(\{[\s\S]*?\})\s*```/);
        jsonString = match ? match[1] : jsonString;
      } else if (jsonString.includes('```')) {
        const match = jsonString.match(/```\s*(\{[\s\S]*?\})\s*```/);
        jsonString = match ? match[1] : jsonString;
      }

      const yokaiData = JSON.parse(jsonString) as YokaiDataGenerationResult;

      // データ検証
      if (!yokaiData.name || !yokaiData.description || !yokaiData.likes || !yokaiData.dislikes || !yokaiData.catchphrase || !yokaiData.imagePrompt) {
        throw new Error('必要なデータが不足しています');
      }

      logger.info('OPENAI_API', '妖怪データ生成完了', {
        name: yokaiData.name,
        descriptionLength: yokaiData.description.length,
        likes: yokaiData.likes,
        dislikes: yokaiData.dislikes,
        catchphrase: yokaiData.catchphrase
      });
      return yokaiData;

    } catch (parseError) {
      logger.error('OPENAI_API', 'JSON解析エラー', {
        parseError,
        responsePreview: content.substring(0, 200) + '...'
      });
      throw new Error('OpenAI APIの応答を解析できませんでした');
    }

  } catch (error) {
    logger.error('OPENAI_API', '妖怪データ生成エラー', error);
    throw handleOpenAiError(error);
  }
};
